import React, { useEffect, useState } from 'react';
import api from '../../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Loader2, BookOpen, Users, ChevronDown, ChevronRight, CheckCircle2, XCircle, ExternalLink } from 'lucide-react';
import { fmtDate } from './periodUtils';

export default function BatchDetailPanel({ batchId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openModules, setOpenModules] = useState(false);
  const [student, setStudent] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/training/audit/batch-detail/${batchId}`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [batchId]);

  if (loading) return <div className="py-6 text-center text-slate-400" data-testid="batch-detail-loading"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;
  if (!data) return <p className="text-[12px] text-slate-400 py-3">Could not load batch details.</p>;

  const syll = data.syllabus || {};
  return (
    <div className="space-y-3" data-testid="batch-detail-panel">
      {/* Syllabus */}
      <div className="rounded-lg border border-slate-200 p-3">
        <div className="flex items-center justify-between">
          <p className="text-[12px] font-semibold text-slate-700 flex items-center gap-1.5"><BookOpen className="w-4 h-4 text-violet-600" />Syllabus completion {syll.course_name ? <span className="text-slate-400 font-normal">· {syll.course_name}</span> : ''}</p>
          {syll.linked
            ? <span className="text-[13px] font-bold text-violet-700" data-testid="syllabus-pct">{syll.completion_pct}%</span>
            : <span className="text-[11px] text-slate-400">No syllabus linked</span>}
        </div>
        {syll.linked && (
          <>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden mt-2"><div className="h-full bg-violet-500 rounded-full" style={{ width: `${syll.completion_pct}%` }} /></div>
            <button onClick={() => setOpenModules((v) => !v)} className="text-[11px] text-violet-600 mt-1.5 flex items-center gap-1" data-testid="syllabus-toggle">
              {openModules ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}{syll.completed_count}/{syll.total_count} topics completed — {openModules ? 'hide' : 'view'} topics
            </button>
            {openModules && (
              <div className="mt-2 space-y-2 max-h-56 overflow-y-auto pr-1">
                {(syll.modules || []).map((m, i) => (
                  <div key={i}>
                    <p className="text-[11px] font-semibold text-slate-600">{m.title}</p>
                    <ul className="mt-0.5 space-y-0.5">
                      {m.subtopics.map((st, j) => (
                        <li key={j} className="text-[11px] flex items-center gap-1.5 text-slate-500">
                          {st.completed ? <CheckCircle2 className="w-3 h-3 text-emerald-500" /> : <XCircle className="w-3 h-3 text-slate-300" />}
                          <span className={st.completed ? 'text-slate-700' : ''}>{st.name}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Students */}
      <div className="rounded-lg border border-slate-200 p-3">
        <p className="text-[12px] font-semibold text-slate-700 flex items-center gap-1.5 mb-2"><Users className="w-4 h-4 text-violet-600" />Students ({data.roster.length}) · {data.sessions_conducted} sessions conducted <span className="text-slate-400 font-normal">— click a student for day-wise attendance</span></p>
        <div className="max-h-52 overflow-y-auto">
          <table className="w-full text-[11px]">
            <thead><tr className="text-left text-slate-400 border-b border-slate-100"><th className="py-1 pr-2">Student</th><th className="py-1 px-2">Enrol #</th><th className="py-1 px-2 text-right">Present</th><th className="py-1 px-2 text-right">Absent</th><th className="py-1 pl-2 text-right">%</th></tr></thead>
            <tbody>
              {data.roster.map((s) => (
                <tr key={s.student_id} onClick={() => setStudent(s)} className="border-b border-slate-50 hover:bg-violet-50 cursor-pointer" data-testid={`student-row-${s.student_id}`}>
                  <td className="py-1.5 pr-2 font-medium text-slate-700">{s.name}</td>
                  <td className="py-1.5 px-2 text-slate-500">{s.enrollment_no || '—'}</td>
                  <td className="py-1.5 px-2 text-right text-emerald-600 tabular-nums">{s.present}</td>
                  <td className="py-1.5 px-2 text-right text-rose-600 tabular-nums">{s.absent}</td>
                  <td className="py-1.5 pl-2 text-right tabular-nums font-semibold">{s.total ? `${s.attendance_pct}%` : '—'}</td>
                </tr>
              ))}
              {data.roster.length === 0 && <tr><td colSpan={5} className="py-4 text-center text-slate-400">No students enrolled.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Per-student day-wise attendance modal */}
      <Dialog open={!!student} onOpenChange={(o) => !o && setStudent(null)}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-hidden flex flex-col" data-testid="student-attendance-modal">
          <DialogHeader><DialogTitle className="text-sm">{student?.name} — attendance ({student?.present}/{student?.total} present)</DialogTitle></DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-3">
            <div className="grid grid-cols-1 gap-1.5" data-testid="student-academics">
              <div className="flex items-center justify-between px-2.5 py-1.5 rounded-md bg-slate-50 text-[12px]">
                <span className="text-slate-500">PDP</span><span className="font-medium text-slate-700">{student?.pdp || '—'}</span>
              </div>
              <div className="flex items-center justify-between px-2.5 py-1.5 rounded-md bg-slate-50 text-[12px]">
                <span className="text-slate-500">Mock Interview</span><span className="font-medium text-slate-700">{student?.mock_interview || '—'}</span>
              </div>
              <div className="flex items-center justify-between px-2.5 py-1.5 rounded-md bg-slate-50 text-[12px]">
                <span className="text-slate-500">Interview Link</span>
                {student?.mock_interview_link
                  ? <a href={student.mock_interview_link} target="_blank" rel="noreferrer" className="text-violet-600 font-medium inline-flex items-center gap-1 hover:underline max-w-[200px] truncate" data-testid="student-interview-link"><ExternalLink className="w-3 h-3" />Open link</a>
                  : <span className="text-slate-400">—</span>}
              </div>
            </div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Day-wise attendance</p>
            {(student?.days || []).length === 0 ? <p className="text-[12px] text-slate-400 py-4 text-center">No attendance records.</p> : (
              <div className="grid grid-cols-2 gap-1.5">
                {student.days.map((d, i) => (
                  <div key={i} className={`flex items-center justify-between px-2.5 py-1.5 rounded-md text-[11px] ${d.status === 'present' ? 'bg-emerald-50 text-emerald-700' : d.status === 'absent' ? 'bg-rose-50 text-rose-700' : 'bg-slate-50 text-slate-500'}`}>
                    <span>{fmtDate(d.date)}</span><span className="capitalize font-medium">{d.status || '—'}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
